import { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import PiBaseUrlInterceptor from './interceptors/PiBaseUrl.interceptor';

const singleton = Symbol();
const singletonEnforcer = Symbol();
const baseUrlInterceptor = new PiBaseUrlInterceptor();

class PiInstance {
  private _httpClient: AxiosInstance;
  private getTokenFunc: {
    (options: GetTokenSilentlyOptions & { detailedResponse: true }): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
    (options: GetTokenSilentlyOptions): Promise<GetTokenSilentlyVerboseResponse | string>;
  };

  constructor(enforcer: typeof singletonEnforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }

    this._httpClient = axios.create({
      baseURL: '',
      timeout: 30000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this._httpClient.interceptors.request.use(baseUrlInterceptor.intercept);
    this._httpClient.interceptors.request.use(async config => await this.intercept(config));
  }

  static get currentInstance(): PiInstance {
    // Try to get an efficient singleton
    if (!this[singleton]) {
      this[singleton] = new PiInstance(singletonEnforcer);
    }

    return this[singleton];
  }

  setTokenFunc(getToken) {
    this.getTokenFunc = getToken;
  }

  get instance(): AxiosInstance {
    return this._httpClient;
  }

  async intercept(config: AxiosRequestConfig) {
    if (!this.getTokenFunc) {
      return config;
    }

    try {
      const token = await this.getTokenFunc();
      config.headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      console.log(e);
    }
    return config;
  }
}

export default PiInstance.currentInstance;
